import React from 'react';

import { useIntl } from 'react-intl';
import { Col, DatePicker, Form, Input, InputNumber, Row, Select, Switch } from 'antd';
import PropTypes from 'prop-types';

import Photos from '@src/packages/events/components/EventComponents/components/Photos';
import Signature from '@src/packages/events/components/EventComponents/components/Signature';
import Title from 'components/BoxContainer/components/Title';
import { KeyValueWidget } from 'components/KeyValueWidget';
import SectionHeader from 'components/SectionHeader';
import IntlMessages from 'util/IntlMessages';

import { dynamicFormFactory } from './constants';
import FormTag from './FormTag';
import ListSelectInput from './ListSelectInput';
import PhotoUploadInput from './PhotoUploadInput';
import SignatureInput from './SignatureInput';
import { filterSelectOption } from './selectFilterOption';
import styles from './styles.module.less';

/**
 * A dynamic form component that renders form fields based on the provided formData.
 * @param {Object[]} formData - An array of objects representing EVENT_TYPE DOCUMENT.
 * @param {string} formName - The name of the form.
 * @param {string} formTitle - The title of the form.
 * @param {Object} props - Additional props to be passed to the Form component.
 * @returns {JSX.Element} - The rendered dynamic form.
 */
const DynamicForm = ({
  divisionId,
  formData = {},
  formName,
  formTitle,
  organizationId,
  photoService,
  ...props
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const data = dynamicFormFactory(formData);

  const renderFormItem = item => {
    switch (item.type) {
      case 'text':
        return (
          <Input
            autoComplete={item?.autoComplete}
            placeholder={item?.placeholder}
            className="gx-w-100"
          />
        );
      case 'number':
        return <InputNumber type="number" placeholder={item?.placeholder} className="gx-w-100" />;
      case 'toggle':
        return <Switch checked={false} />;
      case 'select':
        return (
          <Select
            placeholder={item?.placeholder}
            showSearch
            optionFilterProp="children"
            filterOption={filterSelectOption}
          >
            {item.options.map((option, idx) => (
              <Select.Option key={idx} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        );
      case 'listSelect':
        return (
          <ListSelectInput
            divisionId={divisionId}
            optionListId={item.optionListId}
            organizationId={organizationId}
            placeholder={item?.placeholder}
          />
        );
      case 'tag':
        return <FormTag />;
      case 'date':
        return (
          <DatePicker
            className="gx-w-100"
            showTime={item.includesTime ? { format: 'HH:mm' } : false}
            format={item.includesTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD'}
            showSecond={false}
          />
        );
      case 'photo':
        return (
          <PhotoUploadInput
            divisionId={divisionId}
            inputId={`photo-upload-${item.id}`}
            maxCount={item.maxCount}
            maxSize={item.maxSize}
            organizationId={organizationId}
            origin={item.origin}
            photoService={photoService}
          />
        );
      case 'signature':
        return (
          <SignatureInput
            divisionId={divisionId}
            organizationId={organizationId}
            photoService={photoService}
            includeClarification={item.component.componentProps?.includeClarification}
          />
        );
      default:
        return null;
    }
  };

  const getAuxProps = item => {
    switch (item.type) {
      case 'toggle':
        return {
          valuePropName: 'checked',
          initialValue: item?.checked ?? false,
        };
      default:
        return {};
    }
  };

  const validatePhotos = item => (_, value) => {
    if (value?.some(photo => photo?.uploading)) {
      return Promise.reject(
        new Error(intl.formatMessage({ id: 'dynamic.form.photo.uploading.message' })),
      );
    }
    if (item.maxCount && value?.length > item.maxCount) {
      return Promise.reject(
        new Error(
          intl.formatMessage(
            { id: 'dynamic.form.photo.maxCount.message' },
            { maxCount: item.maxCount },
          ),
        ),
      );
    }
    return Promise.resolve();
  };

  const buildRules = item => {
    const output = [];
    if (item.required) {
      output.push({
        required: true,
        // Photo values are arrays; without `type: 'array'` an emptied list counts as filled.
        ...(item.type === 'photo' && { type: 'array' }),
        message: intl.formatMessage({ id: 'form.required' }),
      });
    }

    if (item.validator) {
      output.push({
        validator: item.validator,
      });
    }

    if (item.type === 'signature') {
      output.push({
        validator: (_, value) =>
          value?.uploading
            ? Promise.reject(
                new Error(intl.formatMessage({ id: 'dynamic.form.photo.uploading.message' })),
              )
            : item.required && !value?.url
              ? Promise.reject(new Error(intl.formatMessage({ id: 'form.required' })))
              : Promise.resolve(),
      });
    }
    if (item.type === 'photo') {
      output.push({ validator: validatePhotos(item) });
    }

    return output;
  };

  const sortedData = data.sort((a, b) => a.order - b.order);

  return (
    <Form name={formName} form={form} {...props}>
      <div className="gx-guarnic-pb-2">
        <span className="gx-guarnic-headline-2">{formTitle}</span>
      </div>
      <Row gutter={16}>
        {sortedData.map(item => (
          <React.Fragment key={item.id}>
            <Col span={12}>
              <Title.LabelForm required={item?.required} value={item.label} />
              <Form.Item
                rules={buildRules(item)}
                {...getAuxProps(item)}
                name={[item.id, item.label]}
              >
                {renderFormItem(item)}
              </Form.Item>
            </Col>
          </React.Fragment>
        ))}
      </Row>
    </Form>
  );
};

DynamicForm.Display = ({ forms = [] }) => {
  const renderFormItem = item => {
    if (
      (!item.value && item.type !== 'toggle') ||
      (typeof item.value === 'object' && item?.value?.length === 0)
    ) {
      return '-';
    }

    switch (item.type) {
      case 'text':
        return <span>{item.value}</span>;
      case 'number':
        return <span>{item.value}</span>;
      case 'toggle':
        return item.value ? <IntlMessages id="general.yes" /> : <IntlMessages id="general.no" />;
      case 'select':
      case 'listSelect':
        return <span>{item.value.join(', ')}</span>;
      case 'tag':
        return <span>{item.value.join(', ')}</span>;
      case 'date':
        return (
          <IntlMessages
            id="common.date.time.value"
            values={{
              date: item.value,
            }}
          />
        );
      case 'photo':
        return <Photos component={item.component} showTitle={false} />;
      case 'signature':
        return <Signature component={item.component} />;
      default:
        return null;
    }
  };

  const getComponents = form => {
    const data = dynamicFormFactory(form);
    let sortedData = data?.sort((a, b) => a?.order - b?.order);
    return sortedData.map(item => (
      <KeyValueWidget
        key={item.id}
        label={<Title.LabelForm value={item.label} />}
        value={renderFormItem(item)}
      />
    ));
  };

  return (
    <>
      {forms.map(form => (
        <React.Fragment key={form.id}>
          <SectionHeader title={form.name}>
            <div className={styles.formDisplayComponents}>{getComponents(form)}</div>
          </SectionHeader>
        </React.Fragment>
      ))}
    </>
  );
};

DynamicForm.propTypes = {
  divisionId: PropTypes.string,
  formData: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.oneOf([
      'text',
      'number',
      'toggle',
      'select',
      'listSelect',
      'tag',
      'date',
      'photo',
      'signature',
    ]),
    autoComplete: PropTypes.string,
    placeholder: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string),
    includesTime: PropTypes.bool,
    required: PropTypes.bool,
    validator: PropTypes.func,
    checked: PropTypes.bool,
    label: PropTypes.string,
    order: PropTypes.number,
  }),
  formName: PropTypes.string,
  formTitle: PropTypes.string,
  organizationId: PropTypes.string,
  photoService: PropTypes.shape({
    remove: PropTypes.func.isRequired,
    upload: PropTypes.func.isRequired,
  }),
};

DynamicForm.Display.propTypes = {
  forms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.shape({ url: PropTypes.string }),
      ]),
      type: PropTypes.oneOf([
        'text',
        'number',
        'toggle',
        'select',
        'listSelect',
        'tag',
        'date',
        'photo',
        'signature',
      ]),
      includesTime: PropTypes.bool,
      order: PropTypes.number,
    }),
  ),
};

export default DynamicForm;
